import { set } from "lodash";

// this will include the entire array for array fields if one of the items has changed
export const dirtyValues = <
  TData extends Record<string, any>,
>(
    data: TData,
    dirtyItems: { [K in keyof TData]?: any },
    alwaysDirty: string[] = [],
  ): Partial<TData> => {
  const dirtyItemsEntries = Object.entries(dirtyItems);

  const allDirtyEntries = [
    ...alwaysDirty.map((field) => [field, true]),
    ...dirtyItemsEntries,
  ] as [string, unknown][];

  return allDirtyEntries.reduce((dirtyData, [key, isDirty]) => {
    if (!Object.prototype.hasOwnProperty.call(data, key)) return dirtyData;

    const value = data[key];

    // Case 1: primitive dirty flag
    if (typeof isDirty === "boolean") {
      if (isDirty) {
        dirtyData[key] = value;
      }
      return dirtyData;
    }

    // ✅ Case 2: array of objects — include full array if any entry is dirty
    if (Array.isArray(value) && typeof isDirty === "object" && isDirty !== null) {
      const isAnyItemDirty = Object.values(isDirty).some(Boolean);
      if (isAnyItemDirty) {
        dirtyData[key] = value;
      }
      return dirtyData;
    }

    // Case 3: nested object (non-array)
    if (typeof isDirty === "object" && isDirty !== null) {
      const nested = dirtyValues(value, isDirty);
      if (Object.keys(nested).length > 0) {
        dirtyData[key] = nested;
      }
    }

    return dirtyData;
  }, {} as Partial<TData>);
};

interface FieldData {
  fields: string[];
  data: Record<string, unknown>;
  dirtyFields: Record<string, unknown>;
}

export function dirtyFieldValues({ fields, data, dirtyFields }: FieldData) {
  const dirtyData = dirtyValues(data, dirtyFields);
  if (Object.keys(dirtyData).length === 0) {
    return {};
  }

  return fields.reduce((acc, indField) => {
    if (Object.prototype.hasOwnProperty.call(dirtyData, indField)) {
      set(acc, indField, dirtyData[indField]);
    }
    return acc;
  }, {});
}
